import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { getOptimizedImageUrl } from "../lib/cms";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { label: "Træfældning", href: "/traefaeldning" },
    { label: "Beskæring", href: "/beskaering" },
    { label: "Rådgivning", href: "/raadgivning" },
    { label: "Opsætning af redekasser", href: "/opsaetning-af-redekasser" },
  ];

  const subLinks = [
    { label: "Træfældning", href: "/traefaeldning", desc: "Sikker fældning, klatring og oprydning" },
    { label: "Beskæring", href: "/beskaering", desc: "Kroneløft, reduktion og bortkørsel" },
    { label: "Rådgivning", href: "/raadgivning", desc: "Faglig vurdering af træer" },
    { label: "Opsætning af redekasser", href: "/opsaetning-af-redekasser", desc: "Montering højt i træer" },
    { label: "Kontakt", href: "/kontakt", desc: "Få et uforpligtende tilbud" },
  ];

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled
          ? "bg-[#fbf9f5]/95 backdrop-blur-md shadow-sm border-b border-[#E7DFD2]"
          : "bg-[#fbf9f5] border-b border-[#EAE3D6]"
      }`}
    >
      {/* Top micro-bar for direct contact og reassurance */}
      <div className="hidden desktop:block bg-[#F1ECE2] text-[#41554A] text-xs py-1.5 border-b border-[#E5DDCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-6 font-medium">
            <span>Dækker hele Syddanmark fra Gram</span>
            <span className="text-[#A59B8B]">•</span>
            <span>Forsikret op til 10 mio. kr.</span>
            <span className="text-[#A59B8B]">•</span>
            <span>10+ års professionel erfaring</span>
          </div>

          <div className="flex items-center gap-6">
            <a
              href="mailto:info@casperstraeservice.dk"
              className="text-[#36493F] hover:text-[#D9691E] transition-colors whitespace-nowrap flex items-center gap-2 font-medium"
            >
              <i className="fa-regular fa-envelope text-[#D9691E]" aria-hidden="true" />
              <span>info@casperstraeservice.dk</span>
            </a>
            <span className="text-[#CDC4B5]">|</span>
            <a
              href="tel:42169230"
              className="hover:text-[#D9691E] font-bold transition-colors whitespace-nowrap flex items-center gap-2 text-[#152E22]"
            >
              <i className="fa-solid fa-phone text-[#D9691E]" aria-hidden="true" />
              <span>42 16 92 30</span>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between min-h-[4.5rem] desktop:min-h-[6.5rem] py-2">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 focus:outline-none py-1"
            aria-label="Caspers Træservice forside"
          >
            <img
              src={getOptimizedImageUrl(
                "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/caspers-logo.png",
                600,
                90
              )}
              alt="Caspers Træservice logo"
              className="site-logo"
            />
          </Link>

          {/* Desktop Navigation — strictly hidden below 1200px as required */}
          <nav
            aria-label="Hovednavigation"
            className="hidden desktop:flex items-center gap-1 xl:gap-2"
          >
            {navLinks.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`px-3.5 py-2 rounded-md text-sm font-semibold transition-all duration-200 relative ${
                    active
                      ? "text-[#152E22] bg-[#EAE2D4] shadow-inner font-bold"
                      : "text-[#344C3F] hover:text-[#152E22] hover:bg-[#EFE8DC]"
                  }`}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                  {active && (
                    <span className="absolute bottom-0 left-3.5 right-3.5 h-0.5 bg-[#D9691E] rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Desktop Right CTA (1200px+) */}
          <div className="hidden desktop:flex items-center gap-4">
            <a
              href="tel:42169230"
              className="inline-flex items-center gap-2 text-white font-bold text-sm tracking-wide bg-[#1E4231] hover:bg-[#285741] px-4 py-2.5 rounded-lg border border-[#35684E] transition-all duration-200 shadow-sm whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#D9691E]"
            >
              <i className="fa-solid fa-phone text-[#D9691E]" aria-hidden="true" />
              <span className="whitespace-nowrap">42 16 92 30</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 text-white font-bold text-sm tracking-wide bg-[#D9691E] hover:bg-[#C25B15] px-5 py-2.5 rounded-lg transition-all duration-200 shadow-md whitespace-nowrap hover:shadow-lg transform active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-white"
            >
              <span>Kontakt</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile / Tablet Controls (<1200px) */}
          <div className="flex desktop:hidden items-center gap-2">
            <a
              href="tel:42169230"
              aria-label="Ring til Caspers Træservice"
              className="hidden min-[667px]:inline-flex items-center gap-1.5 bg-[#1E4231] hover:bg-[#285741] text-white text-xs sm:text-sm font-bold px-3 py-2 rounded-lg border border-[#1E4231] whitespace-nowrap shadow-sm"
            >
              <i className="fa-solid fa-phone text-[#D9691E]" aria-hidden="true" />
              <span className="whitespace-nowrap">42 16 92 30</span>
            </a>

            {/* Burger toggle button — strictly visible below 1200px */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? "Luk menu" : "Åbn menu"}
              className="w-11 h-11 text-[#152E22] flex items-center justify-center cursor-pointer focus:outline-none focus:ring-0 active:bg-transparent"
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? "fa-xmark" : "fa-bars"} text-xl`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer (shows ONLY when toggled and on screens <1200px) */}
      {mobileMenuOpen && (
        <div className="desktop:hidden border-t border-[#E5DDCF] bg-[#fbf9f5] px-4 pt-4 pb-6 shadow-2xl animate-in fade-in slide-in-from-top duration-200">
          <nav aria-label="Mobil navigation" className="space-y-1">
            {navLinks.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-base font-semibold transition-colors ${
                    active
                      ? "text-[#152E22] bg-[#EAE2D4] font-bold border-l-4 border-[#D9691E]"
                      : "text-[#284334] hover:text-[#152E22] hover:bg-[#EFE8DC]"
                  }`}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              to="/kontakt"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg text-base font-semibold transition-colors ${
                isActive("/kontakt")
                  ? "text-[#152E22] bg-[#EAE2D4] font-bold border-l-4 border-[#D9691E]"
                  : "text-[#284334] hover:text-[#152E22] hover:bg-[#EFE8DC]"
              }`}
              aria-current={isActive("/kontakt") ? "page" : undefined}
            >
              Kontakt
            </Link>

            {/* Mobile Contact Information */}
            <div className="pt-4 border-t border-[#E8E1D5] mt-3 space-y-3">
              <div className="text-xs text-[#455A4F] px-4 leading-relaxed">
                <p className="font-bold text-[#152E22] text-sm">Caspers Træservice</p>
                <p>Folevej 23, 6510 Gram</p>
                <p>CVR: 37232521</p>
              </div>

              <div className="pt-1 px-2">
                <a
                  href="tel:42169230"
                  className="flex items-center justify-center gap-2 bg-[#1E4231] text-white py-3 rounded-lg font-bold text-sm border border-[#1E4231] whitespace-nowrap shadow-sm hover:bg-[#285741] w-full"
                >
                  <i className="fa-solid fa-phone text-[#D9691E]" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring op</span>
                </a>
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
